import { lazy } from 'react';
import { isCallActive, onCallIdle } from '../lib/callActivity';

/**
 * 청크 로드 실패(배포 후 구버전 모듈 404) 시 1회 자동 복구를 지원하는 lazy 래퍼
 *
 * index.html 의 moowoo_recover 스크립트와 동일한 sessionStorage 잠금 및 _cb 쿼리 방식을 공유하여
 * 무한 루프 없이 최신 배포본 index.html 을 1회 갱신합니다.
 */
export function lazyWithRetry(componentImport) {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      // 1. 실패 원인이 청크 로드 관련 에러인지 판별
      const errorMessage = error?.message || '';
      const isChunkError =
        errorMessage.includes('dynamically imported module') ||
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('Importing a module script failed') ||
        errorMessage.includes('ChunkLoadError');

      // 청크 로드 실패가 아니면(일반 컴포넌트 오류 등) 즉시 throw 하여 ErrorBoundary 로 전달
      if (!isChunkError) {
        throw error;
      }

      // 2. sessionStorage 및 URL 의 _cb 잠금 확인 (moowoo_recover 공유)
      try {
        const currentUrl = new URL(window.location.href);

        // 이미 _cb 쿼리가 붙어있으면 1회 재시도 실패로 간주하고 무한 루프 차단
        if (currentUrl.searchParams.has('_cb')) {
          throw error;
        }

        const last = sessionStorage.getItem('moowoo_recover');
        const now = Date.now();

        // 잠금이 없거나 60초가 지난 경우 1회만 자동 새로고침(최신 index.html 갱신)
        if (!last || now - Number(last) >= 60000) {
          if (isCallActive()) {
            console.log('[새 버전] 통화 중 — 새로고침 보류');
            onCallIdle(() => {
              setTimeout(() => {
                if (!isCallActive()) {
                  const reloadTime = Date.now();
                  sessionStorage.setItem('moowoo_recover', reloadTime.toString());
                  currentUrl.searchParams.set('_cb', reloadTime.toString());
                  window.location.replace(currentUrl.toString());
                }
              }, 3000);
            });
            return new Promise(() => {});
          }

          sessionStorage.setItem('moowoo_recover', now.toString());
          currentUrl.searchParams.set('_cb', now.toString());
          window.location.replace(currentUrl.toString());

          // 새로고침 진행 중 불필요한 후속 렌더링 중단
          return new Promise(() => {});
        }
      } catch (recoveryErr) {
        // sessionStorage 접근 불가 또는 throw 된 원래 error 전달
        if (recoveryErr === error) throw error;
      }

      // 이미 1회 복구를 시도했거나 복구 불가한 경우 상위 ErrorBoundary 로 전달
      throw error;
    }
  });
}
